import React, { useEffect, useState, useRef } from "react";
import { Container } from "./style";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Form,
  Input,
  Modal,
  Avatar,
  message,
  Tag,
  Alert,
  Select,
} from "antd";
import {
  LeftOutlined,
  TagOutlined,
  EditOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import ReactRouterPrompt from "react-router-prompt";
import MockPhone from "../../../components/MockPhone/index";
import ScheduleMenu from "../../../components/ScheduleMenu/index";
import { LinkPreviewCard, LinkPreviewStatus, useLinkPreview } from "../../../components/LinkPreviewField";
import UserAvatar from "../../../assets/User.png";
import LinkIcon from "../../../assets/link.png";
import logo_practice1 from "../../../assets/logo_practice1.png";
import logo_practice2 from "../../../assets/logo_practice2.png";
import { gql, useMutation, useLazyQuery } from "@apollo/client";
import { setQuoteData } from "../../../store/features/CreateQuote";
import useSubscriptionCheck from "../../../hooks/useSubscriptionCheck";
import SubscriptionModal from "../../../components/SubscriptionModal";

const { TextArea } = Input;

const CREATE_QUOTE = gql`
  mutation createQuote($createQuoteInput: CreateQuoteInput!) {
    createQuote(payload: $createQuoteInput) {
      _id
    }
  }
`;
const UPDATE_QUOTE = gql`
  mutation updateQuote($updateQuoteInput: UpdateQuoteInput!) {
    updateQuote(payload: $updateQuoteInput) {
      _id
    }
  }
`;

const GROUPS_BY_COACH = gql`
  query GroupsByCoach($coachId: String!, $isArchived: Boolean) {
    groupsByCoach(coachId: $coachId, isArchived: $isArchived) {
      _id
      name
    }
  }
`;

export default function CreateQuote() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [createQuote] = useMutation(CREATE_QUOTE);
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const [groups, setGroups] = useState([]);
  const location = useLocation();
  const rowData = useSelector((state) => state.quote.quoteData);
  const rowDataRef = useRef(rowData);

  // Subscription check
  const {
    showModal,
    checkAndExecute,
    handleSubscriptionSuccess,
    handleModalCancel,
    upgradePromptKind,
  } = useSubscriptionCheck();
  rowDataRef.current = rowData;
  const quoteId = rowData?._id;

  const [fetchGroups] = useLazyQuery(GROUPS_BY_COACH, {
    variables: { coachId: userInfo.coach?._id, isArchived: false },
    onCompleted: (data) => {
      if (data?.groupsByCoach) {
        setGroups(data.groupsByCoach);
      }
    },
    fetchPolicy: "network-only",
  });

  // Fetch groups on mount
  useEffect(() => {
    if (userInfo.coach?._id) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.coach?._id]);

  // Form
  const [form] = Form.useForm();

  useEffect(() => {
    if (location.pathname === "/CreateQuote" && rowDataRef.current._id) {
      dispatch(setQuoteData({}));
    }
    if (location.pathname === "/EditQuote" && !rowDataRef.current._id) {
      navigate("/CreateQuote");
    }
  }, [location, dispatch, navigate]);

  // Update quote
  useEffect(() => {
    if (form && rowData._id) {
      // 根据 rowData 的 visibility 数据构建表单初始值
      let visibilityValue = "ALL_SUBSCRIBERS"; // 默认值
      if (rowData.visibility === "PUBLIC") {
        visibilityValue = "ALL";
      } else if (rowData.visibilityType === "ALL_SUBSCRIBERS") {
        visibilityValue = "ALL_SUBSCRIBERS";
      } else if (
        rowData.visibilityType === "GROUP" &&
        rowData.visibilityGroupId
      ) {
        visibilityValue = `GROUP_${rowData.visibilityGroupId}`;
      } else if (rowData.visibility === "PRIVATE") {
        visibilityValue = "ALL_SUBSCRIBERS";
      }
      form.setFieldsValue({
        ...rowData,
        visibility: visibilityValue,
      });
    }
  }, [rowData, form]);

  const contentValue = Form.useWatch("content", form);
  const visibleValue = Form.useWatch("visibility", form);
  const linkValue = Form.useWatch("link", form);
  const linkPreview = useLinkPreview(linkValue);

  useEffect(() => {
    console.log("rowData", rowDataRef.current);
    if (
      (contentValue && contentValue !== rowDataRef.current.content) ||
      (visibleValue && visibleValue !== rowDataRef.current.visibleValue) ||
      (linkValue && linkValue !== rowDataRef.current.link)
    ) {
      setIsChanged(true);
    }
  }, [contentValue, visibleValue, linkValue]);

  useEffect(() => {
    if (loading) {
      setIsChanged(false);
    }
  }, [loading]);

  const onSubmit = async ({ values, status, scheduledAt }) => {
    setLoading(true);
    try {
      if (!quoteId) {
        const param = {
          status,
          coach: userInfo.coach?._id,
          publishedAt: new Date(),
          content: values.content,
          link: values.link,
          visibility: "PRIVATE",
          visibilityType: "ALL_SUBSCRIBERS",
        };

        // 根据选择设置 visibility 相关参数
        const visibilityValue = values.visibility;
        if (visibilityValue?.startsWith("GROUP_")) {
          param.visibilityType = "GROUP";
          param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
        }

        if (scheduledAt) {
          param.scheduledAt = scheduledAt;
        }
        await createQuote({
          variables: {
            createQuoteInput: param,
          },
        });
        message.success("Insight has been created!");
      } else {
        const param = {
          _id: quoteId,
          status,
          coach: userInfo.coach?._id,
          content: values.content,
          link: values.link,
          visibility: "PRIVATE",
          visibilityType: "ALL_SUBSCRIBERS",
        };

        // 根据选择设置 visibility 相关参数
        const visibilityValue = values.visibility;
        if (visibilityValue?.startsWith("GROUP_")) {
          param.visibilityType = "GROUP";
          param.visibilityGroupId = visibilityValue.replace("GROUP_", "");
        }

        if (scheduledAt) {
          param.scheduledAt = scheduledAt;
        }
        await updateQuote({
          variables: {
            updateQuoteInput: param,
          },
        });
        message.success("Insight has been updated!");
      }
      dispatch(setQuoteData({}));
      navigate("/contentManager?tabName=2");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    console.log("Success:", values);
    // Check subscription before publishing
    checkAndExecute(() => {
      onSubmit({ values, status: "PUBLISHED" });
    });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setLoading(false);
  };

  const onDraft = () => {
    const values = form.getFieldsValue();
    onSubmit({ values, status: "DRAFTED" });
  };

  const onBack = () => {
    navigate("/contentManager?tabName=2");
  };

  return (
    <Container forceRender>
      <ReactRouterPrompt when={isChanged}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <Modal
              title="Unsaved changes"
              open
              footer={[
                <div key="flex-between" className="flex-between">
                  <Button
                    key="discard"
                    danger
                    loading={loading}
                    onClick={() => {
                      setIsChanged(false);
                      onConfirm();
                    }}
                  >
                    Discard Changes
                  </Button>
                  <div key="div">
                    <Button key="back" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button
                      key="save"
                      type="primary"
                      loading={loading}
                      onClick={onDraft}
                    >
                      Save Draft
                    </Button>
                  </div>
                </div>,
              ]}
            >
              <p>
                You are about to leave this page with unsaved changes. Would you
                like to save these changes before leaving?
              </p>
            </Modal>
          )
        }
      </ReactRouterPrompt>
      <div className="page-form">
        <div className="left-side">
          <Button
            className="btn-back"
            icon={<LeftOutlined />}
            type="text"
            onClick={onBack}
          >
            Content Manager
          </Button>

          <div className="page-title">
            {!quoteId ? "Create a new Insight" : "Update an Insight page"}
            {/* <Spin
              className="ml-10"
              spinning={quoteId && listQuotesLoading}
            ></Spin> */}
          </div>
          <Form
            form={form}
            name="quoteForm"
            layout="vertical"
            size="large"
            className="full-width"
            initialValues={{
              visibility: "ALL_SUBSCRIBERS",
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Form.Item
              label="Enter your insight"
              name="content"
              rules={[{ required: true, message: "Please input insight" }]}
            >
              <TextArea
                showCount
                maxLength={500}
                placeholder="Empty"
                style={{ resize: "none", height: 250 }}
              />
            </Form.Item>
            <Form.Item
              label="Link"
              name="link"
              rules={[
                {
                  pattern: /^(https?:\/\/)?([\w-]+\.+[\w-]+)+([/?].*)?$/,
                  message: "Please enter a valid link with a valid domain",
                },
              ]}
            >
              <Input placeholder="Please add a link" />
            </Form.Item>
            <LinkPreviewStatus status={linkPreview.status} />
            <Form.Item
              label="Visible to"
              name="visibility"
              rules={[{ required: true, message: "Please select visibility" }]}
            >
              <Select placeholder="Select visibility" size="large">
                <Select.Option value="ALL_SUBSCRIBERS">
                  All Subscribers
                </Select.Option>
                {groups.map((group) => (
                  <Select.Option key={group._id} value={`GROUP_${group._id}`}>
                    {group.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {rowData.status === "SCHEDULED" && rowData.scheduledAt && (
              <div>
                <Alert
                  className="full-width clock-tag"
                  message={`This insight is scheduled to be published at ${new Date(
                    rowData.scheduledAt
                  ).toLocaleString()}.`}
                  icon={<ClockCircleOutlined />}
                  type="success"
                  showIcon
                />
              </div>
            )}
            <Form.Item>
              <ScheduleMenu
                id={quoteId}
                showSchedule={!quoteId || rowData.status !== "PUBLISHED"}
                form={form}
                loading={loading}
                onSubmit={onSubmit}
              ></ScheduleMenu>
              <Button
                block
                className="mt-16"
                loading={loading}
                onClick={onDraft}
              >
                Save as draft
              </Button>
            </Form.Item>
          </Form>
        </div>
        <div className="right-side">
          <MockPhone showTabbar>
            <div className="info-box gray-all">
              <div className="flex-between mb-10">
                <div className="c-gray">By {userInfo?.coach?.name}</div>
                <TagOutlined />
              </div>
              <div className="info-content">
                "Is there a simple way to display a color bitmap in grayscale
                with just HTML/CSS?"
              </div>
              <div className="text-right">
                <Button type="primary" icon={<EditOutlined />}>
                  Write
                </Button>
              </div>
            </div>
            <div className="flex-between mb-10">
              <div>Today's Insights</div>
              <Button type="text">
                <b>More</b>
              </Button>
            </div>
            <div className="info-box">
              <div className="flex-between mb-10">
                <div>
                  <Avatar
                    src={userInfo?.coach?.profile_image || UserAvatar}
                    className="mr-10"
                  />
                  {userInfo?.coach?.name}
                  <span className="c-gray ml-10">
                    {new Date().toISOString().slice(0, 10)}
                  </span>
                </div>
                <TagOutlined />
              </div>
              <div className="info-content">{contentValue || "Empty"}</div>
              {linkPreview.status === "ready" || linkPreview.status === "loading" ? (
                <LinkPreviewCard
                  className="insight-link-preview"
                  status={linkPreview.status}
                  preview={linkPreview.preview}
                />
              ) : (
                !!linkValue && (
                  <div className="align-items-center">
                    <img className="link-image mr-10" src={LinkIcon} alt="Link" />
                    <span className="c-primary fs-14 bold">{linkValue}</span>
                  </div>
                )
              )}
            </div>
            <div className="flex-between mb-10 gray-all">
              <div>Practices Recommended for you</div>
              <Button type="text">
                <b>More</b>
              </Button>
            </div>
            <div className="flex-between mb-10 gray-all">
              <img
                className="practice-image"
                src={logo_practice2}
                alt="Gratitude Journal"
              />
              <div className="fs-13">
                <div className="bold mb-10">
                  <Tag color="#D37773">Gratitude</Tag>
                  <i>Premium</i>
                </div>
                <h4 className="mb-10">Gratitude Journal</h4>
                <div>Reflect on what you are grateful for every day</div>
                <div className="flex-between mt-10">
                  <div className="c-gray fs-12">6 min</div>
                  <div className="c-gray fs-12">323 students</div>
                </div>
              </div>
            </div>
            <div className="flex-between gray-all">
              <img
                className="practice-image"
                src={logo_practice1}
                alt="Practice Success Story"
              />
              <div className="fs-13">
                <div className="bold mb-10">
                  <Tag color="#A38BB6">Grit</Tag>
                  <i>Premium</i>
                </div>
                <h4 className="mb-10">Success Story</h4>
                <div>
                  We learn from experience by incorporating our experiences...
                </div>
                <div className="flex-between mt-10">
                  <div className="c-gray fs-12">6 min</div>
                  <div className="c-gray fs-12">423 students</div>
                </div>
              </div>
            </div>
          </MockPhone>
        </div>
      </div>
      <SubscriptionModal
        open={showModal}
        onSuccess={handleSubscriptionSuccess}
        onCancel={handleModalCancel}
        checkoutPromptKind={upgradePromptKind ?? "trial"}
      />
    </Container>
  );
}
